import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { RADIUS_KM_MAX } from "@app/shared";
import { companiesApi, type RadiusFilter } from "./api";
import { IndustryFilterSelect } from "./IndustryFilterSelect";
import { Button, Card, Input, PageTitle } from "../../components/ui";
import { QueryView } from "../../components/states";
import { RadiusSearchMap, type Coords } from "../../components/map/CompanyMap";

// 地図から範囲検索（F-19）。中心を地図でクリックし、半径kmで円内の企業をさがす。
export function MapSearchPage() {
  const [center, setCenter] = useState<Coords>({ latitude: null, longitude: null });
  const [radiusKm, setRadiusKm] = useState(3);
  const [keyword, setKeyword] = useState("");
  const [industryCode, setIndustryCode] = useState("");
  const [filter, setFilter] = useState<RadiusFilter | null>(null);

  const hasCenter = center.latitude != null && center.longitude != null;

  const query = useQuery({
    queryKey: ["companies", "public", "nearby", filter],
    queryFn: () => companiesApi.nearby(filter as RadiusFilter),
    enabled: filter !== null,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageTitle title="地図から範囲検索" subtitle="中心と半径を指定して受入企業をさがせます。" />
        <div className="flex gap-2">
          <Link to="/map">
            <Button variant="ghost">全体マップ</Button>
          </Link>
          <Link to="/">
            <Button variant="ghost">一覧にもどる</Button>
          </Link>
        </div>
      </div>

      <Card className="space-y-3">
        <RadiusSearchMap
          center={center}
          radiusKm={radiusKm}
          onPickCenter={(lat, lng) => setCenter({ latitude: lat, longitude: lng })}
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
            半径
            <input
              type="range"
              min={1}
              max={RADIUS_KM_MAX}
              step={1}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
            />
            <span className="tabular-nums">{radiusKm} km</span>
          </label>
          <Input
            placeholder="キーワード（任意）"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="!w-48"
          />
          <IndustryFilterSelect value={industryCode} onChange={setIndustryCode} className="!w-40" />
          <Button
            disabled={!hasCenter}
            onClick={() =>
              setFilter({
                latitude: center.latitude as number,
                longitude: center.longitude as number,
                radiusKm,
                q: keyword.trim() || undefined,
                industryCode: industryCode || undefined,
                page: 1,
              })
            }
          >
            この範囲で検索
          </Button>
          {!hasCenter && (
            <span className="text-xs text-slate-400">
              地図をクリックして中心を指定してください。
            </span>
          )}
        </div>
      </Card>

      {filter && (
        <QueryView
          query={query}
          emptyWhen={(d) => d.items.length === 0}
          emptyMessage="この範囲に受入企業は見つかりませんでした。半径を広げてみてください。"
        >
          {(data) => (
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-600">
                この範囲の受入企業: {data.total} 件
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {data.items.map((c) => (
                  <Link key={c.id} to={`/companies/${c.id}`}>
                    <Card className="h-full transition hover:-translate-y-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-lg font-extrabold text-brand-dark">{c.name}</h3>
                        <span className="whitespace-nowrap rounded-pill bg-sky-light px-2.5 py-0.5 text-xs font-bold text-brand-dark">
                          約 {c.distanceKm} km
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-400">
                        {c.area}・{c.industryLabel}
                      </p>
                      <p className="mt-2 line-clamp-2 text-sm text-slate-600">{c.description}</p>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </QueryView>
      )}
    </div>
  );
}
